import * as Clipboard from 'expo-clipboard';
import { router, useFocusEffect } from 'expo-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  createHouseholdApi,
  fetchHouseholdApi,
  fetchReferralInfoApi,
  joinHouseholdApi,
  type HouseholdInfoDto,
  type ReferralInfo,
} from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAuthStore } from '@/store/auth-store';
import { useBudgetStore } from '@/store/budget-store';
import { useGoalsStore } from '@/store/goals-store';
import { memberNameOf, useHouseholdStore } from '@/store/household-store';
import { setHouseholdActive, syncHouseholdNow } from '@/sync/household-sync';
import { colors, radius, spacing } from '@/theme/tokens';
import { formatPln, timeAgoPl } from '@/utils/format';

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Administrator',
  PARTNER: 'Współdzielący',
  CHILD: 'Dziecko',
};

interface ActivityItem {
  id: string;
  memberId: string;
  text: string;
  createdAt: string;
}

/** Shared household — mock: wsp_lne_gospodarstwo/screen.png */
export default function HouseholdScreen() {
  const { t } = useTranslation();
  const household = useHouseholdStore((s) => s.household);
  const account = useAuthStore((s) => s.account);
  const entries = useBudgetStore((s) => s.entries);
  const { goals, contributions } = useGoalsStore();

  // Server household (realny sync) — feature-detected; 404 on old backend = none.
  const [serverHousehold, setServerHousehold] = useState<HouseholdInfoDto | null>(null);
  const [referral, setReferral] = useState<ReferralInfo | null>(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // Which code was last copied ('hh' = household invite, 'ref' = referral) — so
  // each copy button flips its own checkmark, not both.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);

  // Invite link (/dolacz?kod=…) or onboarding left a code behind — prefill the
  // "mam kod" input so the invited person only confirms, never retypes.
  const pendingHouseholdCode = useAppMetaStore((s) => s.pendingHouseholdCode);
  const setPendingHouseholdCode = useAppMetaStore((s) => s.setPendingHouseholdCode);
  useEffect(() => {
    if (!pendingHouseholdCode) return;
    setJoinOpen(true);
    setJoinCode(pendingHouseholdCode);
    void setPendingHouseholdCode(null);
  }, [pendingHouseholdCode, setPendingHouseholdCode]);

  useFocusEffect(
    useCallback(() => {
      if (!account) {
        setServerHousehold(null);
        setReferral(null);
        return;
      }
      let cancelled = false;
      (async () => {
        const [res, ref] = await Promise.all([fetchHouseholdApi(), fetchReferralInfoApi()]);
        if (cancelled) return;
        setServerHousehold(res.ok && res.data ? res.data : null);
        setReferral(ref.ok && ref.data ? ref.data : null);
      })();
      return () => {
        cancelled = true;
      };
    }, [account]),
  );

  const copyCode = async (key: string, code: string) => {
    await Clipboard.setStringAsync(code);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Create a SERVER household so this phone becomes the one that hands out the
  // invite code (the other phone joins with it). Answers "nie ma miejsca na
  // wpisanie kodu" — one side creates, the other enters, both on this screen.
  const onCreate = async () => {
    setCreating(true);
    const res = await createHouseholdApi(household?.name ?? 'Nasze gospodarstwo');
    setCreating(false);
    if (res.ok && res.data) {
      setServerHousehold(res.data);
      setHouseholdActive(res.data.id);
      // Creating unmounts this card (the code lives in the hero card above) —
      // close any open "mam kod" input and scroll up so the fresh invite code
      // is actually on screen (user: created a code and couldn't see it).
      setJoinOpen(false);
      setJoinCode('');
      setJoinError(null);
      scrollRef.current?.scrollTo({ y: 0, animated: true });
      void syncHouseholdNow().catch(() => undefined);
    } else {
      setJoinError(res.message ?? t('household.joinError'));
    }
  };

  const onJoin = async () => {
    setJoining(true);
    setJoinError(null);
    const res = await joinHouseholdApi(joinCode.trim().toUpperCase());
    setJoining(false);
    if (res.ok && res.data) {
      setServerHousehold(res.data);
      setHouseholdActive(res.data.id);
      setJoinOpen(false);
      setJoinCode('');
      void syncHouseholdNow().catch(() => undefined);
    } else {
      setJoinError(res.message ?? t('household.joinError'));
    }
  };

  if (!household) return null;

  // How many accounts actually joined the server household (owner counts as 1).
  const joinedCount = serverHousehold?.members.length ?? 0;

  const activity: ActivityItem[] = [
    ...entries.slice(0, 6).map((e) => ({
      id: e.id,
      memberId: e.authorMemberId,
      text: t('household.activityExpense', {
        category: t(`budget.categories.${e.categoryId}`),
        amount: formatPln(e.amountGr),
      }),
      createdAt: e.createdAt,
    })),
    ...contributions.slice(0, 6).map((c) => ({
      id: c.id,
      memberId: c.memberId,
      text: t('household.activityContribution', {
        goal: goals.find((g) => g.id === c.goalId)?.name ?? '',
        amount: formatPln(c.amountGr),
      }),
      createdAt: c.createdAt,
    })),
  ]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 5);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
      >
        <ScreenHeader title={t('household.title')} />

        {/* Hero */}
        <Card style={{ gap: spacing.sm }}>
          <AppText variant="headlineMd">{household.name}</AppText>
          <AppText variant="bodyMd">{t('household.heroSubtitle')}</AppText>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
            <Ionicons name={serverHousehold ? 'sync-circle' : 'sync-outline'} size={18} color={colors.primary} />
            <AppText variant="labelMd" color={colors.primary}>
              {serverHousehold ? t('household.syncOn') : t('household.syncSoon')}
            </AppText>
          </View>
          {/* Server household active -> show the copyable invite code */}
          {serverHousehold && (
            <Pressable
              accessibilityRole="button"
              onPress={() => void copyCode('hh', serverHousehold.inviteCode)}
              style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs }}
            >
              <View style={{ flex: 1 }}>
                <AppText variant="labelMd" style={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t('household.inviteCode')}
                </AppText>
                <AppText variant="headlineSm" color={colors.primary}>
                  {serverHousehold.inviteCode}
                </AppText>
              </View>
              <Ionicons name={copiedKey === 'hh' ? 'checkmark-outline' : 'copy-outline'} size={22} color={colors.primary} />
            </Pressable>
          )}
        </Card>

        {/* Referral code — separate from the household code; always visible so it's
            copyable on its own (owner request: the 50 zł code was hidden here). */}
        {account && referral && (
          <Card style={{ gap: spacing.xs }}>
            <Pressable
              accessibilityRole="button"
              onPress={() => void copyCode('ref', referral.code)}
              style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}
            >
              <View style={{ flex: 1 }}>
                <AppText variant="labelMd" style={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  {t('household.referralCode')}
                </AppText>
                <AppText variant="headlineSm" color={colors.primary}>
                  {referral.code}
                </AppText>
              </View>
              <Ionicons name={copiedKey === 'ref' ? 'checkmark-outline' : 'copy-outline'} size={22} color={colors.primary} />
            </Pressable>
            <AppText variant="labelMd" color={colors.outline}>
              {t('household.referralCodeHint')}
            </AppText>
          </Card>
        )}

        {/* Guest: explain that connecting two phones needs login (owner: "nie ma
            miejsca na wpisanie kodu" — as a guest there's no server to sync to). */}
        {!account && (
          <Card style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
              <Ionicons name="phone-portrait-outline" size={20} color={colors.primary} />
              <AppText variant="labelLg" color={colors.navy}>
                {t('household.connectHeader')}
              </AppText>
            </View>
            <AppText variant="bodyMd">{t('household.guestConnect')}</AppText>
            <PrimaryButton label={t('household.loginCta')} onPress={() => router.push('/auth/login')} />
          </Card>
        )}

        {/* Connect two phones — logged in but not yet in a server household.
            One side creates the code, the other enters it, both right here. */}
        {account && !serverHousehold && (
          <Card style={{ gap: spacing.sm, backgroundColor: colors.infoBg }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
              <Ionicons name="people-outline" size={20} color={colors.primary} />
              <AppText variant="labelLg" color={colors.navy}>
                {t('household.connectHeader')}
              </AppText>
            </View>
            <AppText variant="bodyMd">{t('household.connectExplain')}</AppText>
            <PrimaryButton label={t('household.createCode')} disabled={creating} onPress={onCreate} />
            {!joinOpen ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => setJoinOpen(true)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 40 }}
              >
                <Ionicons name="enter-outline" size={22} color={colors.primary} />
                <AppText variant="labelLg" color={colors.navy} style={{ flex: 1 }}>
                  {t('household.haveCode')}
                </AppText>
                <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
              </Pressable>
            ) : (
              <>
                <TextField
                  label={t('household.codeLabel')}
                  placeholder="HH-XXX-XXX"
                  autoCapitalize="characters"
                  value={joinCode}
                  onChangeText={setJoinCode}
                />
                {joinError ? (
                  <AppText variant="labelMd" color={colors.error}>
                    {joinError}
                  </AppText>
                ) : null}
                {/* Owner worry (test 14.07): "czy moje dane się skasują?" — the
                    merge is row-level and upsert-only (household-sync), so say
                    it plainly BEFORE the join button. */}
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
                  <Ionicons name="information-circle-outline" size={16} color={colors.outline} style={{ marginTop: 2 }} />
                  <AppText variant="labelMd" color={colors.outline} style={{ flex: 1 }}>
                    {t('household.joinMergeNote')}
                  </AppText>
                </View>
                <PrimaryButton label={t('household.joinCta')} disabled={joinCode.trim().length < 4 || joining} onPress={onJoin} />
              </>
            )}
          </Card>
        )}

        {/* Members */}
        <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
          {t('household.members')}
        </AppText>
        <Card style={{ padding: 0 }}>
          {household.members.map((member, idx) => {
            // A non-owner is "invited, not joined" until someone actually joins the
            // server household (owner + ≥1 = joinedCount ≥ 2). Honest: shows they're
            // not an active user yet, just invited.
            const isPending = member.role !== 'OWNER' && joinedCount < 2;
            return (
              <View
                key={member.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.sm,
                  padding: spacing.md,
                  borderTopWidth: idx === 0 ? 0 : 1,
                  borderTopColor: colors.divider,
                }}
              >
                <View
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: radius.full,
                    backgroundColor: member.role === 'OWNER' ? colors.navy : colors.primaryTile,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AppText variant="labelLg" color={member.role === 'OWNER' ? '#FFFFFF' : colors.navy}>
                    {member.firstName.charAt(0)}
                  </AppText>
                </View>
                <View style={{ flex: 1 }}>
                  <AppText variant="labelLg">{member.firstName}</AppText>
                  <AppText variant="bodyMd" color={isPending ? colors.outline : undefined}>
                    {isPending ? t('household.memberPending') : ROLE_LABELS[member.role]}
                  </AppText>
                </View>
                {isPending && <Ionicons name="time-outline" size={20} color={colors.outline} />}
              </View>
            );
          })}
        </Card>

        <PrimaryButton label={t('household.invite')} onPress={() => router.push('/household-invite')} />

        {/* Activity feed */}
        <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
          {t('household.activity')}
        </AppText>
        <Card style={{ padding: 0 }}>
          {activity.map((item, idx) => (
            <View
              key={item.id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: spacing.sm,
                padding: spacing.md,
                borderTopWidth: idx === 0 ? 0 : 1,
                borderTopColor: colors.divider,
              }}
            >
              <Ionicons name="cart-outline" size={22} color={colors.navy} />
              <View style={{ flex: 1 }}>
                <AppText variant="bodyLg">
                  <AppText variant="labelLg">{memberNameOf(household, item.memberId)}</AppText> {item.text}
                </AppText>
                <AppText variant="labelMd">{timeAgoPl(item.createdAt)}</AppText>
              </View>
            </View>
          ))}
        </Card>

        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/household-settings')}
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, minHeight: 44 }}
        >
          <Ionicons name="settings-outline" size={20} color={colors.navy} />
          <AppText variant="labelLg" color={colors.navy}>
            {t('household.settings')}
          </AppText>
        </Pressable>
      </ScrollView>
    </SafeAreaView>
  );
}
